import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

export interface ConnectFormField {
  name: string;
  label: string;
  type: "text" | "password" | "number" | "switch" | "textarea" | "select" | "key-value";
  placeholder?: string;
  required?: boolean;
  default?: unknown;
  gridCol?: string; // e.g. "2/3", "1/3", "1/2"
  showWhen?: { field: string; value: unknown };
  hideWhen?: { field: string; value: unknown };
  options?: { label: string; value: string }[]; // for select type
  keyPlaceholder?: string; // for key-value type
  valuePlaceholder?: string; // for key-value type
}

interface ConnectFormRendererProps {
  fields: ConnectFormField[];
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  /** When editing, password fields show "Leave blank to keep existing" placeholder */
  isEdit?: boolean;
}

/**
 * Renders a dynamic connection form based on JSONB field definitions from all_connectors.
 * Supports text, password, number, switch, and textarea fields with conditional visibility.
 */
const ConnectFormRenderer = ({ fields, values, onChange, isEdit }: ConnectFormRendererProps) => {
  const isFieldVisible = (field: ConnectFormField): boolean => {
    if (field.showWhen) {
      return values[field.showWhen.field] === field.showWhen.value;
    }
    if (field.hideWhen) {
      return values[field.hideWhen.field] !== field.hideWhen.value;
    }
    return true;
  };

  // Group consecutive fields with gridCol into grid rows
  const renderFields = () => {
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < fields.length) {
      const field = fields[i];

      if (!isFieldVisible(field)) {
        i++;
        continue;
      }

      // Check if this field and the next field(s) form a grid row
      if (field.gridCol) {
        const gridFields: ConnectFormField[] = [field];
        let j = i + 1;
        while (j < fields.length && fields[j].gridCol && isFieldVisible(fields[j])) {
          gridFields.push(fields[j]);
          j++;
        }

        // Calculate grid columns
        const totalParts = gridFields.reduce((sum, f) => {
          const [num, den] = (f.gridCol || "1/1").split("/").map(Number);
          return sum + (num / den);
        }, 0);

        const cols = totalParts <= 1 ? gridFields.length : Math.round(1 / (1 / gridFields.length));

        elements.push(
          <div key={`grid-${i}`} className={`grid grid-cols-${gridFields.length} gap-3`} style={{ gridTemplateColumns: gridFields.map(f => {
            const [num, den] = (f.gridCol || "1/1").split("/").map(Number);
            return `${num}fr`;
          }).join(" ") }}>
            {gridFields.map((gf) => renderSingleField(gf))}
          </div>
        );
        i = j;
      } else {
        elements.push(renderSingleField(field));
        i++;
      }
    }

    return elements;
  };

  const renderSingleField = (field: ConnectFormField) => {
    const value = values[field.name];

    if (field.type === "switch") {
      return (
        <div key={field.name} className="flex items-center gap-2">
          <Switch
            checked={(value as boolean | undefined) ?? (field.default as boolean | undefined) ?? false}
            onCheckedChange={(v) => onChange(field.name, v)}
          />
          <Label className="text-sm">{field.label}</Label>
        </div>
      );
    }

    if (field.type === "textarea") {
      return (
        <div key={field.name} className="space-y-1.5">
          <Label>{field.label}</Label>
          <Textarea
            placeholder={field.placeholder}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(field.name, e.target.value)}
            rows={3}
          />
        </div>
      );
    }

    if (field.type === "select" && field.options) {
      return (
        <div key={field.name} className="space-y-1.5">
          <Label>{field.label}</Label>
          <Select
            value={(value as string) ?? (field.default as string) ?? ""}
            onValueChange={(v) => onChange(field.name, v)}
          >
            <SelectTrigger>
              <SelectValue placeholder={field.placeholder || "Select..."} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (field.type === "key-value") {
      const pairs = (value as { key: string; value: string }[]) ?? [];
      return (
        <div key={field.name} className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label>{field.label}</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => onChange(field.name, [...pairs, { key: "", value: "" }])}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add
            </Button>
          </div>
          {pairs.length === 0 && (
            <p className="text-xs text-muted-foreground">{field.placeholder || "No entries yet"}</p>
          )}
          <div className="space-y-1.5">
            {pairs.map((pair, idx) => (
              <div key={idx} className="flex items-center gap-1.5">
                <Input
                  className="h-8 text-sm"
                  placeholder={field.keyPlaceholder || "Key"}
                  value={pair.key}
                  onChange={(e) => {
                    const updated = [...pairs];
                    updated[idx] = { ...pair, key: e.target.value };
                    onChange(field.name, updated);
                  }}
                />
                <Input
                  className="h-8 text-sm"
                  placeholder={field.valuePlaceholder || "Value"}
                  value={pair.value}
                  onChange={(e) => {
                    const updated = [...pairs];
                    updated[idx] = { ...pair, value: e.target.value };
                    onChange(field.name, updated);
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-red-400"
                  onClick={() => {
                    const updated = pairs.filter((_, i) => i !== idx);
                    onChange(field.name, updated);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div key={field.name} className="space-y-1.5">
        <Label>{field.label}</Label>
        <Input
          type={field.type === "number" ? "number" : field.type === "password" ? "password" : "text"}
          placeholder={
            field.type === "password" && isEdit
              ? "Leave blank to keep existing"
              : field.placeholder
          }
          value={field.type === "number" ? ((value as number | undefined) ?? (field.default as number | undefined) ?? "") : ((value as string | undefined) ?? "")}
          onChange={(e) =>
            onChange(
              field.name,
              field.type === "number" ? (parseInt(e.target.value) || 0) : e.target.value,
            )
          }
        />
      </div>
    );
  };

  return <div className="space-y-3">{renderFields()}</div>;
};

export default ConnectFormRenderer;

/**
 * Builds initial form values from connect_form field definitions,
 * optionally pre-filling from existing connection params.
 */
export function buildFormValues(
  fields: ConnectFormField[],
  existingParams?: Record<string, unknown>,
  existingName?: string,
): Record<string, unknown> {
  const values: Record<string, unknown> = {};

  for (const field of fields) {
    if (field.name === "name") {
      values.name = existingName ?? "";
    } else if (existingParams && field.name in existingParams) {
      values[field.name] = existingParams[field.name];
    } else if (field.name === "useConnectionString" && existingParams?.connection_string) {
      values.useConnectionString = true;
    } else if (field.default !== undefined) {
      values[field.name] = field.default;
    } else if (field.type === "switch") {
      values[field.name] = false;
    } else if (field.type === "number") {
      values[field.name] = 0;
    } else if (field.type === "key-value") {
      values[field.name] = [];
    } else {
      values[field.name] = "";
    }
  }

  return values;
}

/**
 * Extracts connection_params from form values (excludes "name" and "useConnectionString").
 */
export function extractConnectionParams(
  fields: ConnectFormField[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  const useConnStr = values.useConnectionString as boolean;

  for (const field of fields) {
    if (field.name === "name" || field.name === "useConnectionString") continue;

    // Skip fields that are hidden based on conditions
    if (field.showWhen && values[field.showWhen.field] !== field.showWhen.value) continue;
    if (field.hideWhen && values[field.hideWhen.field] === field.hideWhen.value) continue;

    const val = values[field.name];
    // Skip empty passwords (preserve existing)
    if (field.type === "password" && !val) continue;
    // Skip empty strings
    if (val === "" || val === undefined || val === null) continue;

    params[field.name] = val;
  }

  return params;
}
